import {storeToRefs} from 'pinia';
import type {RouteLocationNormalized} from 'vue-router';
import {defineNuxtRouteMiddleware, navigateTo, createError} from '#app';
import {callOnce, useAuth, redirectToSignIn} from '#imports';
import {useComplianceImmersion} from '~/composables/useComplianceImmersion';
import {RouteNames} from '~/configuration/routeNames';
import {MenuItemStatus} from '~/models/MenuItems.types';
import {useMenuStore} from '~/stores/compliance-menu.store';

export default defineNuxtRouteMiddleware(async (to: RouteLocationNormalized) => {
    const complianceImmersion = useComplianceImmersion();
    const menuStore = useMenuStore();
    const authStore = useAuth();
    const {menuItemLinks} = storeToRefs(menuStore);

    const connectedCompanyId = authStore.getSelectedIdentity?.freelancerCompanyId || authStore.getSelectedIdentity?.umbrellaCompanyId;
    // For route without id like "/freelancer/company"
    if (!to.params.id && connectedCompanyId) {
        return navigateTo({
            name: getDefaultRouteName(),
            params: {
                id: connectedCompanyId,
            },
            query: {...to.query},
        });
    }

    if (to.params.id && to.params.id.length !== 24 && connectedCompanyId) {
        return navigateTo({
            name: Object.values(RouteNames)[to.params.id as unknown as number] ?? getDefaultRouteName(),
            params: {
                id: connectedCompanyId,
            },
            query: {...to.query},
        });
    }

    if (!to.params.id) {
        return navigateTo('/', {external: true});
    }

    // We check immersion mode to know which data to use to get the compliance menu
    await complianceImmersion.checkIfImmersionMode(to);

    // If we had never get the data for the menu, we take them
    if (!menuItemLinks || !menuItemLinks.value || menuItemLinks.value.length === 0) {
        try {
            await callOnce('menu', async () => await menuStore.updateMenu(to.params.id as string), {mode: 'navigation'});
        } catch (e: any) {
            if (e?.statusCode === 403) {
                throw createError({statusCode: 403});
            }
            if (e?.statusCode === 401) {
                return redirectToSignIn();
            }
            throw e;
        }
    }

    // We check the status of the menu to be sure that our current page is accessible
    if (to && to.name) {
        if (menuItemLinks.value.length > 0) {
            const key = to.name.toString();
            const menuItem = menuItemLinks.value.find((menuItem) => menuItem.routeName === key);

            if (!menuItem || menuItem.status === MenuItemStatus.LOCKED) {
                const query = {...to.query};
                return navigateTo({name: getDefaultRouteName(), query});
            }
        }
    }
    function getDefaultRouteName() {
        return RouteNames.PERSONAL_DETAILS;
    }
});
